import config from "@/config"
import { buildCanonicalUrl } from "@/core/site"

export type JsonLdObject = Record<string, unknown>

/**
 * JSON for a `<script type="application/ld+json">`. `<` becomes `\u003c` so page text can never
 * close the tag; U+2028/2029 are escaped because they are not valid in older JS string literals.
 */
export function serializeJsonLd(data: JsonLdObject): string {
  return JSON.stringify(data)
    .replace(/</g, "\\u003c")
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029")
}

const context = "https://schema.org"
const absolute = (path: string) => new URL(path, config.core.websiteUrl).href

function organizationRef(): JsonLdObject {
  return { "@type": "Organization", name: config.core.projectName, url: buildCanonicalUrl("/") }
}

/** `sameAs` only with real accounts; never invent profiles. */
export function organizationJsonLd(sameAs?: readonly string[]): JsonLdObject {
  return {
    "@context": context,
    ...organizationRef(),
    logo: absolute("/icon-512.png"),
    ...(sameAs?.length ? { sameAs } : {}),
  }
}

export interface Crumb {
  name: string
  /** Omit on the last crumb (the current page). */
  path?: string
}

export function breadcrumbJsonLd(crumbs: readonly Crumb[]): JsonLdObject {
  if (crumbs.length < 2) throw new Error("BreadcrumbList needs at least two items")
  return {
    "@context": context,
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((crumb, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: crumb.name,
      ...(crumb.path ? { item: buildCanonicalUrl(crumb.path) } : {}),
    })),
  }
}

export interface WebPageInput {
  name: string
  path: string
  /** ISO date, same source as the visible "Last reviewed" line. */
  dateModified: string
}

/** Plain page entity: no rating, offer or review types (compare-seo.md section 3). */
export function webPageJsonLd(input: WebPageInput): JsonLdObject {
  return {
    "@context": context,
    "@type": "WebPage",
    name: input.name,
    url: buildCanonicalUrl(input.path),
    dateModified: input.dateModified,
  }
}

export interface CollectionPageInput {
  name: string
  path: string
  /** The pages the hub links to, in the order shown. */
  items: readonly { name: string; path: string }[]
}

/** Hub page listing other pages: CollectionPage with an ItemList, no rating, offer or review types. */
export function collectionPageJsonLd(input: CollectionPageInput): JsonLdObject {
  return {
    "@context": context,
    "@type": "CollectionPage",
    name: input.name,
    url: buildCanonicalUrl(input.path),
    mainEntity: {
      "@type": "ItemList",
      itemListElement: input.items.map((item, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: item.name,
        url: buildCanonicalUrl(item.path),
      })),
    },
  }
}

export interface ArticleInput {
  headline: string
  path: string
  image: string
  /** ISO dates, same source as the visible "Last reviewed" line. */
  datePublished: string
  dateModified: string
  /** Locale prefix of the page ("zh"); default English. */
  localePrefix?: string
}

/** Brand author (an Organization pointing at the home page); no invented people. */
export function articleJsonLd(input: ArticleInput): JsonLdObject {
  return {
    "@context": context,
    "@type": "Article",
    headline: input.headline,
    image: [absolute(input.image)],
    datePublished: input.datePublished,
    dateModified: input.dateModified,
    author: organizationRef(),
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": buildCanonicalUrl(input.path, input.localePrefix),
    },
  }
}

/**
 * Entity description only. No `offers` until prices are final and no `aggregateRating`/`review`
 * (we have none), so it is not a rich-result candidate (seo-spec section 5).
 */
export function softwareApplicationJsonLd(): JsonLdObject {
  return {
    "@context": context,
    "@type": "SoftwareApplication",
    name: config.core.projectName,
    url: buildCanonicalUrl("/"),
    applicationCategory: "BusinessApplication",
    operatingSystem: "iOS, Android",
  }
}
